export const TERMS_DOC = {
  title: "Terms of Use / Terms of Service",
  pageTitle: "Terms of Use | Brixigo",
  description:
    "Terms of Use and Terms of Service governing access to websites, applications, and SaaS services provided by Brixigo Technologies Private Limited.",
  effectiveDate: "20 August 2026",
  updated: "20 August 2026",
  intro: [
    "Welcome to the services provided by Brixigo Technologies Private Limited (hereinafter referred to as \"Brixigo\", \"Company\", \"We\", \"Us\" or \"Our\").",
    "These Terms of Use / Terms of Service (hereinafter referred to as \"Terms\") govern your access to and use of the websites, applications, software platforms, products and services made available by Brixigo, including its software-as-a-service offerings (collectively referred to as the \"Services\").",
    "The term \"You\", \"Your\", \"User\" or \"Customer\" refers to the individual accessing or using the Services and, where the Services are subscribed to or used by an organisation, the organisation and its authorised representatives, employees, personnel or users accessing the Services on its behalf.",
    "By accessing, registering for, subscribing to, or using the Services, you acknowledge that you have read, understood and agreed to be bound by these Terms and our Privacy Policy, which forms an integral part of these Terms.",
    "If you do not agree with these Terms, you must not access or use the Services.",
  ],
  sections: [
    {
      heading: "1. Eligibility and Authority",
      paragraphs: ["By using the Services, You represent and warrant that:"],
      ordered: true,
      list: [
        "You are at least eighteen (18) years of age and are legally capable of entering into a binding agreement under applicable law.",
        "You have the legal capacity and authority to accept and be bound by these Terms.",
        "If You are accessing or using the Services on behalf of a company, partnership, limited liability partnership, proprietorship, trust, association or any other legal entity, You represent and warrant that You have the authority to bind such entity to these Terms.",
        "Where an organisation has subscribed to the Services and permits its employees, representatives or other authorised persons to access the Services, the organisation shall be responsible for ensuring that such persons comply with these Terms.",
        "You shall provide information that is accurate, complete and current and shall promptly update such information where necessary.",
        "You shall use the Services only for lawful purposes and in accordance with these Terms and applicable laws and regulations.",
      ],
    },
    {
      heading: "2. Acceptance of Terms",
      paragraphs: [
        "These Terms constitute a legally binding agreement between You and Brixigo concerning Your use of the Services.",
        "By accessing or using the Services, You acknowledge that You have had an opportunity to read these Terms and agree to comply with them.",
        "These Terms, together with any applicable subscription agreement, order form, proposal, statement of work, service-specific terms and the Brixigo Privacy Policy, constitute the agreement governing Your use of the applicable Services.",
        "Where there is a conflict between these Terms and a separately executed written agreement between Brixigo and a Customer, the terms of the separately executed agreement shall prevail to the extent of such conflict.",
      ],
    },
    {
      heading: "3. Description of Services",
      paragraphs: [
        "Brixigo provides software and technology services through a software-as-a-service (\"SaaS\") model.",
        "The Services may include software applications, platforms, dashboards, tools, features, integrations, documentation and related services made available by Brixigo from time to time.",
        "The specific features and functionality available to You may depend upon the subscription, plan, order form or service package selected by You or Your organisation.",
        "Brixigo reserves the right to introduce, modify, enhance, suspend or discontinue particular features or components of the Services from time to time, provided that such changes shall not materially affect contractual commitments expressly agreed with a Customer without appropriate notice.",
      ],
    },
    {
      heading: "4. Limited Right to Use the Services",
      paragraphs: [
        "Subject to Your compliance with these Terms and payment of applicable fees, where applicable, Brixigo grants You a limited, non-exclusive, non-transferable and revocable right to access and use the Services during the applicable subscription period.",
        "This right is granted solely for Your internal business or authorised organisational purposes and does not constitute a sale, transfer or assignment of any intellectual property rights in the Services.",
        "You shall not use the Services for any purpose other than the purpose for which they are provided.",
      ],
    },
    {
      heading: "5. Account Registration and User Credentials",
      paragraphs: [
        "Certain Services may require You to create an account or be provided with login credentials.",
        "You agree to provide accurate and complete information during registration and to keep such information updated.",
        "You are responsible for maintaining the confidentiality and security of Your username, password, authentication credentials and other account information.",
        "You shall not knowingly share Your login credentials with unauthorised persons or permit any person to access the Services through Your account except as expressly authorised.",
        "You are responsible for activities conducted through Your account, except where such activities result directly from a security incident attributable to Brixigo.",
        "If You become aware of any unauthorised access, disclosure of credentials or suspected security breach relating to Your account, You must promptly notify Brixigo at hr@brixigo.com.",
        "Brixigo may suspend or restrict an account where it reasonably believes that the account has been compromised, misused or used in violation of these Terms.",
      ],
    },
    {
      heading: "6. Customer Data and User Content",
      paragraphs: [
        "For purposes of these Terms, \"Customer Data\" means data, information, documents, records, files or other content submitted, uploaded, transmitted or otherwise provided by You or Your authorised users through the Services.",
        "You retain ownership of Your Customer Data.",
        "You grant Brixigo a limited right to host, store, process, transmit and otherwise use Customer Data solely to the extent reasonably necessary to provide, maintain, secure and support the Services and to perform its contractual obligations.",
        "You represent and warrant that You have all necessary rights, permissions and lawful authority to provide Customer Data to Brixigo and to permit Brixigo to process such data for the purposes contemplated by the Services.",
        "Brixigo shall not knowingly use Customer Data for purposes unrelated to providing the Services except where such use is required or permitted by applicable law, these Terms, or a separate written agreement with You.",
      ],
    },
    {
      heading: "7. Customer Responsibilities",
      paragraphs: ["You are responsible for:"],
      ordered: true,
      list: [
        "ensuring that Your use of the Services complies with applicable laws and regulations;",
        "ensuring that the information and Customer Data submitted through the Services is accurate and lawful;",
        "maintaining appropriate access controls for Your users;",
        "protecting Your account credentials and authentication information;",
        "ensuring that Your authorised users use the Services in accordance with these Terms;",
        "reviewing information, reports, outputs or results generated through the Services before relying upon them where human verification is reasonably required; and",
        "maintaining appropriate backups of information where such backups are necessary for Your business operations.",
      ],
      afterParagraphs: [
        "Brixigo shall not be responsible for inaccuracies or losses arising solely from inaccurate, incomplete or unauthorised information supplied by You or Your authorised users.",
      ],
    },
    {
      heading: "8. Restrictions and Prohibited Uses",
      paragraphs: ["You shall not, and shall not permit any third party to:"],
      ordered: true,
      list: [
        "copy, reproduce, modify, distribute, sell, lease, license, sublicense or commercially exploit the Services or any portion thereof except as expressly permitted under these Terms;",
        "reverse engineer, decompile, disassemble or otherwise attempt to derive the source code, underlying algorithms or technical architecture of the Services, except to the extent expressly permitted by applicable law;",
        "attempt to gain unauthorised access to the Services, another user's account, systems, networks or data;",
        "interfere with or disrupt the operation, security or integrity of the Services;",
        "introduce viruses, malware, malicious code or other harmful components into the Services;",
        "use automated means, bots, crawlers, scraping tools or similar mechanisms to access or extract information from the Services except where expressly authorised by Brixigo;",
        "use the Services to transmit or store unlawful, fraudulent, defamatory, obscene, threatening or otherwise prohibited material;",
        "use the Services to infringe the intellectual property, privacy, confidentiality or other rights of any person;",
        "use the Services to attempt to circumvent authentication, access controls, security mechanisms or usage restrictions;",
        "use the Services for any unlawful purpose or in violation of applicable laws and regulations; or",
        "permit any unauthorised person to use the Services through Your account or subscription.",
      ],
      afterParagraphs: [
        "Brixigo reserves the right to investigate suspected violations and take appropriate measures, including suspension or termination of access where reasonably necessary.",
      ],
    },
    {
      heading: "9. Intellectual Property Rights",
      paragraphs: [
        "All intellectual property rights in and to the Services, including the software, source code, object code, algorithms, architecture, interfaces, designs, trademarks, logos, documentation, workflows, features, graphics and other materials developed or provided by Brixigo (collectively, \"Brixigo IP\") shall remain the exclusive property of Brixigo or its licensors.",
        "Nothing contained in these Terms shall be interpreted as transferring ownership of any Brixigo IP to You.",
        "Except for the limited right to use the Services expressly granted under these Terms, You shall acquire no right, title or interest in or to the Brixigo IP.",
        "You shall not remove, alter or obscure copyright, trademark or other proprietary notices appearing in the Services.",
        "Any suggestions, recommendations, feedback or ideas voluntarily provided by You concerning the Services may be used by Brixigo to improve its products and services, provided that such use does not disclose Your confidential information or Customer Data.",
      ],
    },
    {
      heading: "10. Confidentiality",
      paragraphs: [
        "Each party may receive or have access to confidential information belonging to the other party in connection with the Services.",
        "Each party agrees to use reasonable care to protect the other party's confidential information and shall not disclose such information to third parties except where disclosure is necessary to perform its obligations, authorised by the disclosing party, or required by applicable law.",
        "Confidential information does not include information that:",
      ],
      ordered: true,
      list: [
        "is or becomes publicly available without breach of these Terms;",
        "was lawfully known to the receiving party before disclosure;",
        "is independently developed without use of the confidential information; or",
        "is lawfully obtained from a third party without a duty of confidentiality.",
      ],
    },
    {
      heading: "11. Payments and Subscription",
      paragraphs: [
        "Where the Services are provided on a paid subscription basis, the applicable fees, billing frequency, subscription period, taxes and payment terms shall be specified in the applicable order form, subscription agreement, invoice or other commercial document issued or agreed by Brixigo.",
        "Unless otherwise agreed in writing, applicable taxes and statutory charges shall be payable in addition to the subscription fees.",
        "Brixigo may suspend access to paid Services where undisputed amounts remain unpaid beyond the applicable payment due date, subject to any notice or contractual requirements applicable to the Customer.",
        "Subscription fees, once paid, shall be treated in accordance with the applicable subscription or commercial terms agreed between Brixigo and the Customer.",
      ],
    },
    {
      heading: "12. Service Availability and Maintenance",
      paragraphs: [
        "Brixigo will make reasonable efforts to maintain the availability and functionality of the Services.",
        "However, the Services may occasionally be unavailable due to scheduled maintenance, emergency maintenance, upgrades, technical failures, telecommunications failures, internet connectivity issues, security incidents or circumstances beyond Brixigo's reasonable control.",
        "Brixigo may perform scheduled maintenance from time to time and, where reasonably practicable, provide advance notice of planned maintenance that is expected to materially affect availability.",
        "Brixigo does not guarantee that the Services will be uninterrupted or completely error-free.",
      ],
    },
    {
      heading: "13. Third-Party Services and Integrations",
      paragraphs: [
        "The Services may integrate with or contain links to third-party services, applications, platforms or websites.",
        "Where You choose to use a third-party service or integration, Your use of that service may be subject to the third party's separate terms and privacy policy.",
        "Brixigo is not responsible for the operation, availability, security, content or privacy practices of third-party services that are outside Brixigo's reasonable control.",
        "Any third-party integration shall be subject to the technical availability and terms applicable to that integration.",
      ],
    },
    {
      heading: "14. Data Security and Hosting",
      paragraphs: [
        "Brixigo takes reasonable technical and organisational measures to protect information processed through the Services against unauthorised access, alteration, disclosure, destruction or loss.",
        "The Services may be hosted and operated using cloud infrastructure and may utilise security, authentication, access-control, encryption, monitoring and other safeguards appropriate to the nature of the Services.",
      ],
    },
    {
      heading: "15. Privacy and Personal Data",
      paragraphs: [
        "Brixigo's collection, use, storage and processing of personal data is governed by its Privacy Policy, as amended from time to time.",
        "The Privacy Policy forms an integral part of these Terms.",
        "Where Brixigo processes personal data on behalf of a Customer in connection with the Customer's use of the SaaS Services, such processing shall be undertaken in accordance with the applicable agreement, the Customer's lawful instructions and applicable data protection laws.",
        "Brixigo shall maintain reasonable safeguards for personal data processed through the Services.",
      ],
    },
    {
      heading: "16. Cookies and Analytics",
      paragraphs: [
        "Brixigo may use cookies and similar technologies to enable essential functionality, maintain sessions, improve user experience and understand how its website and Services are used.",
        "Brixigo may use Google Analytics or similar analytics functionality to understand website usage, traffic patterns and user interactions.",
        "The use of cookies and analytics technologies is further described in the Brixigo Privacy Policy.",
        "You may configure your browser to refuse or restrict cookies. However, disabling certain cookies may affect the availability or functionality of some portions of the Services.",
      ],
    },
    {
      heading: "17. Compliance with Applicable Laws",
      paragraphs: [
        "You agree to use the Services in compliance with all applicable laws and regulations in India.",
        "Brixigo may take reasonable measures to comply with applicable legal, regulatory, governmental, court or law-enforcement requirements.",
        "Nothing in these Terms is intended to limit any rights or obligations that cannot lawfully be excluded or limited under applicable law.",
      ],
    },
    {
      heading: "18. Suspension and Termination",
      paragraphs: ["Brixigo may suspend or restrict access to the Services where:"],
      ordered: true,
      list: [
        "You materially breach these Terms;",
        "Your use of the Services creates a security risk to Brixigo, another user or the Services;",
        "You use the Services for unlawful or fraudulent activities;",
        "applicable fees remain unpaid in accordance with the agreed payment terms;",
        "suspension is required by law or a competent authority; or",
        "continued access could reasonably cause harm to Brixigo, its users or its systems.",
      ],
      afterParagraphs: [
        "Where reasonably practicable, Brixigo will provide notice before suspension, except where immediate suspension is necessary due to security, legal or other urgent circumstances.",
        "Either party may terminate a subscription or contractual relationship in accordance with the applicable commercial agreement.",
        "Upon termination, Your right to access and use the applicable Services shall cease, subject to any agreed data-export or transition period.",
      ],
    },
    {
      heading: "19. Data Following Termination",
      paragraphs: [
        "Following termination or expiry of the applicable Services, Brixigo may retain Customer Data for a limited period reasonably necessary to complete deletion, comply with legal obligations, resolve disputes, maintain required business records or protect its legitimate interests.",
        "Unless a different period is agreed in writing or required by law, Brixigo intends to delete or anonymise Customer Data within a reasonable period following termination, subject to applicable backup, legal and operational requirements.",
        "Where technically and commercially feasible, Brixigo may provide Customers with a reasonable opportunity to export their Customer Data before deletion.",
      ],
    },
    {
      heading: "20. User Feedback",
      paragraphs: [
        "If You voluntarily provide suggestions, recommendations, comments or feedback concerning the Services, Brixigo may use such feedback for purposes of improving, developing or maintaining its products and services.",
        "Such use shall not give Brixigo any right to disclose Your confidential information or Customer Data.",
      ],
    },
    {
      heading: "21. Disclaimer of Warranties",
      paragraphs: [
        "To the maximum extent permitted by applicable law, the Services are provided on an \"as is\" and \"as available\" basis.",
        "Brixigo does not warrant that:",
      ],
      ordered: true,
      list: [
        "the Services will always be uninterrupted or error-free;",
        "all defects will be corrected immediately;",
        "the Services will meet every particular requirement of every user;",
        "the Services will be available at all times;",
        "the Services will be completely secure or free from vulnerabilities; or",
        "information obtained through the Services will always be complete, accurate or suitable for every particular purpose.",
      ],
      afterParagraphs: [
        "You acknowledge that SaaS and internet-based services may be affected by factors outside Brixigo's reasonable control.",
        "Nothing in these Terms excludes any warranty or protection that cannot lawfully be excluded under applicable law.",
      ],
    },
    {
      heading: "22. No Professional Advice",
      paragraphs: [
        "Unless expressly stated otherwise, information, reports, recommendations or other content made available through the Services are provided for general informational or operational purposes.",
        "Brixigo does not provide legal, tax, accounting, financial, employment or other professional advice merely by providing access to its software or Services.",
        "You should obtain appropriate professional advice where required before making decisions that have legal, financial, employment or regulatory consequences.",
      ],
    },
    {
      heading: "23. Indemnification",
      paragraphs: [
        "To the extent permitted by applicable law, You agree to indemnify and hold harmless Brixigo, its directors, officers, employees, representatives and service providers from claims, losses, liabilities, damages and reasonable expenses arising from:",
      ],
      ordered: true,
      list: [
        "Your material breach of these Terms;",
        "Your unlawful use or misuse of the Services;",
        "Your violation of applicable law;",
        "Your infringement of the rights of a third party; or",
        "Customer Data or other content submitted by You where such claim arises from Your lack of the necessary rights or authority to submit or use such content.",
      ],
    },
    {
      heading: "24. Limitation of Liability",
      paragraphs: [
        "To the maximum extent permitted by applicable law, Brixigo shall not be liable for any indirect, incidental, special, consequential, exemplary or punitive damages, or for loss of profits, revenue, business opportunities, goodwill or anticipated savings arising out of or relating to the use of, or inability to use, the Services.",
        "Brixigo shall not be liable for losses arising from events outside its reasonable control, including failures of telecommunications networks, internet services, third-party infrastructure, force majeure events or unauthorised actions by third parties.",
        "To the maximum extent permitted by applicable law, Brixigo's aggregate liability arising out of or relating to the Services shall be limited to the amount of fees actually paid by the Customer to Brixigo for the affected Services during the twelve (12) months immediately preceding the event giving rise to the claim.",
        "Nothing in these Terms shall exclude or limit liability that cannot legally be excluded or limited under applicable law.",
      ],
    },
    {
      heading: "25. Changes to the Services",
      paragraphs: [
        "Brixigo may modify, update, enhance or discontinue features of the Services from time to time as part of its normal product development and maintenance activities.",
        "Where a change materially affects the Services provided under an existing contractual commitment, Brixigo will make reasonable efforts to provide appropriate notice.",
        "Brixigo may also update these Terms from time to time. The updated version will be published on the applicable website or platform and will become effective from the date stated in the updated Terms.",
        "Your continued use of the Services after the effective date of the updated Terms constitutes acceptance of the revised Terms, to the extent permitted by law.",
      ],
    },
    {
      heading: "26. Publicity",
      paragraphs: [
        "Brixigo shall not publicly identify a Customer as a customer, or use a Customer's name or logo in public marketing materials, without the Customer's prior consent, unless otherwise expressly agreed in writing.",
        "Any agreed publicity shall not disclose the Customer's confidential information.",
      ],
    },
    {
      heading: "27. Third-Party Content",
      paragraphs: [
        "The Services may contain links or references to third-party websites, content or resources.",
        "Such links are provided for convenience only. Brixigo does not necessarily endorse or control third-party content and shall not be responsible for the availability, accuracy, security or privacy practices of third-party websites.",
        "Your use of third-party websites or services is subject to the applicable third party's terms and policies.",
      ],
    },
    {
      heading: "28. Unlawful Activities and Security",
      paragraphs: [
        "Brixigo reserves the right to investigate suspected misuse, security violations or unlawful activities relating to the Services.",
        "Where reasonably necessary and permitted by law, Brixigo may cooperate with law-enforcement authorities, regulators or other competent authorities and may disclose information required by law or lawful governmental request.",
        "Brixigo may also take reasonable steps to protect the security and integrity of its Services, users and infrastructure.",
      ],
    },
    {
      heading: "29. Non-Transferability",
      paragraphs: [
        "Your right to access and use the Services is personal to You or limited to the organisation identified in the applicable subscription or agreement.",
        "You may not assign, transfer, sublicense or otherwise transfer Your rights or obligations under these Terms without Brixigo's prior written consent, except where such restriction is prohibited by applicable law.",
        "Brixigo may assign these Terms as part of a merger, acquisition, corporate restructuring or transfer of substantially all of its relevant business or assets, provided that the assignee assumes the applicable obligations.",
      ],
    },
    {
      heading: "30. Grievance and Contact",
      paragraphs: [
        "If You have any complaint, concern or grievance relating to the Services, these Terms, privacy or the processing of personal data, You may contact Brixigo at:",
      ],
      contact: {
        name: "Brixigo Technologies Private Limited",
        address: "H. No. 13-4-38, Babu Camp, Bellampalli, Telangana – 504251, India",
        email: "hr@brixigo.com",
      },
      afterParagraphs: [
        "Brixigo will make reasonable efforts to acknowledge and address complaints within a reasonable period in accordance with applicable law.",
      ],
    },
    {
      heading: "31. Notices",
      paragraphs: [
        "Brixigo may provide notices to You through the email address associated with Your account, through the Services, or by posting the notice on the applicable Brixigo website or platform.",
        "You are responsible for ensuring that the email address associated with Your account remains accurate and accessible.",
        "Notices to Brixigo may be sent to hr@brixigo.com, unless a different contact is specified in the applicable agreement.",
      ],
    },
    {
      heading: "32. Force Majeure",
      paragraphs: [
        "Brixigo shall not be responsible for any delay or failure to perform its obligations caused by circumstances beyond its reasonable control, including natural disasters, acts of government, war, civil disturbance, epidemics, pandemics, strikes, power failures, internet or telecommunications failures, cyberattacks by third parties, infrastructure failures or other events beyond Brixigo's reasonable control.",
      ],
    },
    {
      heading: "33. Severability",
      paragraphs: [
        "If any provision of these Terms is determined by a competent court or authority to be invalid, unlawful or unenforceable, that provision shall be modified or interpreted to the minimum extent necessary to make it enforceable, and the remaining provisions shall continue in full force and effect.",
      ],
    },
    {
      heading: "34. Waiver",
      paragraphs: [
        "Failure by Brixigo to exercise or enforce any right or provision under these Terms shall not constitute a waiver of that right or provision.",
        "Any waiver must be expressly made in writing.",
      ],
    },
    {
      heading: "35. Entire Agreement",
      paragraphs: [
        "These Terms, together with the Brixigo Privacy Policy and any applicable subscription agreement, order form, service-specific terms or other written agreement between Brixigo and the Customer, constitute the agreement governing Your use of the Services.",
        "They supersede prior understandings, representations or communications concerning the subject matter covered by these Terms, except where expressly preserved in writing.",
      ],
    },
    {
      heading: "36. Governing Law and Jurisdiction",
      paragraphs: [
        "These Terms shall be governed by and construed in accordance with the laws of India.",
        "Subject to any mandatory dispute-resolution requirements under applicable law, the courts having appropriate jurisdiction in Bengaluru, Karnataka, India, shall have exclusive jurisdiction over disputes arising out of or relating to these Terms or the Services.",
      ],
    },
    {
      heading: "37. Survival",
      paragraphs: [
        "Provisions which by their nature are intended to survive termination or expiry of these Terms shall survive, including provisions concerning intellectual property, confidentiality, Customer Data, limitations of liability, indemnification, governing law and dispute resolution.",
      ],
    },
    {
      heading: "38. Contact Information",
      paragraphs: ["For questions regarding these Terms, the Services or related matters, please contact:"],
      contact: {
        name: "Brixigo Technologies Private Limited",
        address: "H. No. 13-4-38, Babu Camp, Bellampalli, Telangana – 504251, India",
        email: "hr@brixigo.com",
        emailPrefix: "Official Email",
      },
    },
  ],
};
